import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState } from "@/components/common";
import { CopyCanvas, PageTabs } from "@/components/CopyAnnotator";
import { ClipboardCheck, CheckCircle2, Eye, X, Check, Plus, Minus, MessageSquare, Highlighter, Circle, RotateCw, ZoomIn, ZoomOut, Award, Undo2, ChevronLeft, ChevronRight } from "lucide-react";

const MAX_Q = 8;
const CHIPS = Array.from({ length: 17 }, (_, i) => i * 0.5);
const fmtM = (n) => Number(n).toFixed(1);
const STATUS = {
  checked: ["Checked", "text-teal-700"], in_progress: ["In progress (draft)", "text-indigo-600"],
  rescan_requested: ["Re-scan requested", "text-red-600"], pending: ["Not checked yet", "text-amber-600"],
};
const statusOf = (c) => c.status || (c.checked ? "checked" : "pending");

export default function CopyChecking() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["copies"], queryFn: () => get("/copy-submissions") });
  const [openIdx, setOpenIdx] = useState(null);
  if (isLoading) return <PageLoader />;
  const list = data || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["copies"] });
  return (
    <div>
      <PageHeader title="Copy Checking" subtitle="Student answer-copy uploads — every teacher can see status and checked copies" />
      {!list.length ? <EmptyState icon={ClipboardCheck} title="No copies uploaded yet" hint="Uploaded subjective answer copies will appear here." /> : (
        <div className="space-y-3">{list.map((c, i) => <CopyRow key={c.id} c={c} onOpen={() => setOpenIdx(i)} />)}</div>
      )}
      {openIdx != null && list[openIdx] && (
        <EvaluationConsole key={list[openIdx].id} copy={list[openIdx]} idx={openIdx} count={list.length}
          onNav={(d) => setOpenIdx(openIdx + d)} onClose={() => { setOpenIdx(null); refresh(); }} onSaved={refresh} />
      )}
    </div>
  );
}

function CopyRow({ c, onOpen }) {
  const st = statusOf(c);
  const [label, tone] = STATUS[st] || STATUS.pending;
  const total = c.subjective_total_marks || 0;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3" data-testid={`copy-row-${c.id}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="font-bold text-slate-900">{c.student_name}</p>
        <p className="text-xs text-slate-500">{c.test_title}</p>
        <p className={`text-xs font-semibold mt-1 ${tone}`} data-testid={`copy-status-${c.id}`}>
          {label}{st === "checked" && ` by ${c.checked_by_name} · ${fmtM(c.total_marks)}${total ? ` / ${fmtM(total)}` : ""} marks`}
        </p>
      </div>
      {st === "checked" ? (
        <>
          <span className="text-teal-700 font-bold flex items-center gap-1 text-sm" data-testid={`copy-checked-${c.id}`}><CheckCircle2 className="h-4 w-4" /> Checked</span>
          <button onClick={onOpen} data-testid={`copy-view-${c.id}`} className="border border-indigo-200 text-indigo-700 font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1"><Eye className="h-4 w-4" /> View Copy</button>
        </>
      ) : <button onClick={onOpen} data-testid={`copy-check-${c.id}`} className="bg-indigo-800 text-white font-bold px-4 py-2 rounded-lg text-sm">Check & Mark</button>}
    </div>
  );
}

const TOOLS = [
  ["correct", "OK", "Correct", Check], ["wrong", "X", "Wrong", X], ["mark", "+M", "Assign M", Award],
  ["comment", "T", "Comment", MessageSquare], ["highlight", "H", "Highlight", Highlighter], ["circle", "O", "Circle", Circle],
];

function Toolbar({ tool, setTool, onRotate, onZoom, onUndo, readOnly }) {
  const Btn = ({ k, code, label, Icon, on, active }) => (
    <button onClick={on} data-testid={`tool-${k}`} title={label}
      className={`w-14 py-1.5 rounded-lg border text-center ${active ? "bg-emerald-500 border-emerald-400 text-white" : "border-white/10 text-slate-200 hover:bg-white/10"}`}>
      <Icon className="h-4 w-4 mx-auto" /><p className="text-[9px] font-mono font-bold mt-0.5">[{code}]</p><p className="text-[8px] opacity-80 truncate">{label}</p>
    </button>
  );
  return (
    <div className="w-[72px] shrink-0 bg-[#141a33] flex flex-col items-center gap-1.5 py-3 overflow-y-auto">
      {!readOnly && TOOLS.map(([k, code, label, Icon]) => <Btn key={k} k={k} code={code} label={label} Icon={Icon} active={tool === k} on={() => setTool(tool === k ? null : k)} />)}
      <Btn k="rotate" code="90" label="Rotate P" Icon={RotateCw} on={onRotate} />
      <Btn k="zoom-in" code="+" label="Zoom In" Icon={ZoomIn} on={() => onZoom(0.15)} />
      <Btn k="zoom-out" code="-" label="Zoom Out" Icon={ZoomOut} on={() => onZoom(-0.15)} />
      {!readOnly && <Btn k="undo" code="U" label="Undo" Icon={Undo2} on={onUndo} />}
    </div>
  );
}

function EvaluationConsole({ copy, idx, count, onNav, onClose, onSaved }) {
  const readOnly = statusOf(copy) === "checked";
  const { data: pg, isLoading } = useQuery({ queryKey: ["copy-pages", copy.id], queryFn: () => get(`/copy-submissions/${copy.id}/pages`) });
  const [marks, setMarks] = useState(copy.marks?.length ? copy.marks : [null]);
  const [ann, setAnn] = useState(copy.annotations || []);
  const [remarks, setRemarks] = useState(copy.remarks || "");
  const [sel, setSel] = useState(0);
  const [tool, setTool] = useState(null);
  const [page, setPage] = useState(0);
  const [rot, setRot] = useState({});
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const pages = pg?.pages || [];
  const given = Math.round(marks.reduce((s, m) => s + Number(m || 0), 0) * 100) / 100;
  const total = copy.subjective_total_marks || 0;
  const done = marks.filter((m) => m != null).length;

  const addAnn = (a) => {
    if (a.type === "comment") { const text = window.prompt("Comment"); if (!text) return; a.text = text; }
    if (a.type === "mark") { setAnn([...ann.filter((x) => !(x.type === "mark" && x.q === sel)), { ...a, q: sel, page }]); return; }
    setAnn([...ann, { ...a, page }]);
  };
  const save = async (publish) => {
    if (publish && marks.some((m) => m == null)) return toast.error("Give marks to every question before publishing");
    setBusy(true);
    try {
      await post(`/copy-submissions/${copy.id}/check`, { marks, total_marks: given, annotations: ann, remarks, publish });
      toast.success(publish ? "Evaluated result published to student" : "Draft saved"); onSaved();
      if (publish) onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const rescan = async () => {
    const reason = window.prompt("Reason for re-scan (shown to student)", "Copy is unclear, please upload a clearer scan.");
    if (reason == null) return;
    await post(`/copy-submissions/${copy.id}/rescan`, { reason });
    toast.success("Re-scan requested from student"); onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0f1426] flex flex-col" data-testid="marking-screen">
      <header className="bg-[#1d2459] text-white px-4 py-2.5 flex items-center gap-3 shrink-0">
        <span className="h-9 w-9 rounded-lg bg-orange-400 text-white font-black flex items-center justify-center">R</span>
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold truncate">RankTest Teacher Evaluation Console</p>
          <p className="text-[11px] text-indigo-200 truncate">Exam: {copy.test_title}{copy.checked_by_name ? ` · Evaluator: ${copy.checked_by_name}` : ""}</p>
        </div>
        <div className="hidden md:block border border-indigo-400/50 bg-indigo-900/60 rounded-lg px-3 py-1.5 text-xs">
          <p className="font-bold text-amber-300">Student: {copy.student_name}</p>
          <p className="text-indigo-200 text-[10px]">Subjective answer copy · {pages.length || "…"} page(s)</p>
        </div>
        <div className={`rounded-lg px-3 py-2 text-xs font-bold ${readOnly ? "bg-teal-600" : "bg-emerald-600"}`} data-testid="eval-status">
          Status: {readOnly ? "CHECKED" : `IN-PROGRESS (${done}/${marks.length} Checked)`}
        </div>
        <button onClick={onClose} data-testid="marking-close-btn" className="text-indigo-200 hover:text-white"><X className="h-5 w-5" /></button>
      </header>

      <div className="flex-1 flex min-h-0">
        <Toolbar tool={tool} setTool={setTool} readOnly={readOnly} onRotate={() => setRot({ ...rot, [page]: ((rot[page] || 0) + 90) % 360 })}
          onZoom={(d) => setZoom((z) => Math.min(2.5, Math.max(0.5, Math.round((z + d) * 100) / 100)))} onUndo={() => setAnn(ann.slice(0, -1))} />
        <div className="flex-1 flex flex-col min-w-0 bg-slate-300">
          <div className="bg-slate-100 border-b border-slate-300 px-4 py-2 flex items-center gap-3">
            <p className="text-xs font-bold text-slate-700 whitespace-nowrap">Student Answer Copy</p>
            <PageTabs pages={pages} page={page} setPage={setPage} />
            <p className="ml-auto text-[11px] text-slate-500 whitespace-nowrap" data-testid="copy-zoom-level">Zoom: {Math.round(zoom * 100)}%{tool ? ` · Tool: ${tool}` : ""}</p>
          </div>
          <div className="flex-1 overflow-auto">
            {isLoading ? <PageLoader label="Preparing copy pages..." /> : pages[page] &&
              <CopyCanvas src={pages[page]} annotations={ann.filter((a) => (a.page || 0) === page)} marks={marks} rot={rot[page] || 0} zoom={zoom} tool={tool} onAdd={addAnn} readOnly={readOnly} />}
          </div>
        </div>
        <MarksPanel marks={marks} setMarks={setMarks} sel={sel} setSel={setSel} remarks={remarks} setRemarks={setRemarks} given={given} total={total} readOnly={readOnly} />
      </div>

      <footer className="bg-slate-100 border-t border-slate-300 px-4 py-2.5 flex items-center gap-2 shrink-0">
        <button onClick={() => onNav(-1)} disabled={idx === 0} data-testid="prev-student-btn" className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-semibold disabled:opacity-40 flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Previous Student</button>
        <button onClick={() => onNav(1)} disabled={idx >= count - 1} data-testid="next-student-btn" className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-semibold disabled:opacity-40 flex items-center gap-1">Next Student <ChevronRight className="h-4 w-4" /></button>
        {!readOnly && <>
          <button onClick={rescan} data-testid="request-rescan-btn" className="ml-auto px-4 py-2 rounded-lg bg-red-100 border border-red-300 text-red-700 text-sm font-semibold">Request Re-scan</button>
          <button onClick={() => save(false)} disabled={busy} data-testid="save-draft-btn" className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-semibold">Save Draft</button>
          <button onClick={() => save(true)} disabled={busy} data-testid="save-check-btn" className="px-6 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50">{busy ? "Saving..." : "APPROVE & PUBLISH EVALUATED RESULT →"}</button>
        </>}
      </footer>
    </div>
  );
}

function MarksPanel({ marks, setMarks, sel, setSel, remarks, setRemarks, given, total, readOnly }) {
  const setMark = (v) => setMarks(marks.map((x, i) => (i === sel ? v : x)));
  return (
    <div className="w-[380px] shrink-0 bg-white flex flex-col min-h-0 overflow-y-auto">
      <p className="font-display font-bold text-slate-900 px-4 pt-4">Evaluation &amp; Custom Marking Controls</p>
      <div className="px-4 py-3 flex flex-wrap gap-1.5">
        {marks.map((m, i) => (
          <button key={i} onClick={() => setSel(i)} data-testid={`question-row-${i}`}
            className={`px-2.5 py-1 rounded-md text-xs font-bold border ${sel === i ? "bg-blue-100 border-blue-500 text-blue-800" : m != null ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}`}>
            Q.{String(i + 1).padStart(2, "0")} <span data-testid={`question-mark-${i}`}>{m == null ? "—" : fmtM(m)}</span>
          </button>
        ))}
        {!readOnly && <>
          <button onClick={() => { setMarks([...marks, null]); setSel(marks.length); }} data-testid="add-question-btn" className="px-2 py-1 rounded-md text-xs font-bold border border-dashed border-indigo-400 text-indigo-700 flex items-center"><Plus className="h-3 w-3" /> Q</button>
          {marks.length > 1 && <button onClick={() => { setMarks(marks.slice(0, -1)); setSel(0); }} data-testid="remove-question-btn" className="px-2 py-1 rounded-md text-xs font-bold border border-dashed border-red-300 text-red-500 flex items-center"><Minus className="h-3 w-3" /> Q</button>}
        </>}
      </div>
      {!readOnly && (
        <div className="mx-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <p className="font-bold text-slate-800 text-sm">One-Tap Quick Marks — Q.{String(sel + 1).padStart(2, "0")}</p>
          <p className="text-[11px] text-slate-500 mb-2">Max {MAX_Q} marks per question. Use [+M] to stamp it on the copy.</p>
          <div className="grid grid-cols-6 gap-1.5">
            {CHIPS.map((v) => (
              <button key={v} onClick={() => setMark(v)} data-testid={`scale-${v}`} className={`py-1.5 rounded-md text-xs font-bold border ${marks[sel] === v ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:border-blue-400"}`}>{fmtM(v)}</button>
            ))}
          </div>
        </div>
      )}
      <div className="mx-4 mt-3 rounded-xl border border-slate-200 p-3">
        <p className="text-xs font-bold text-slate-700 mb-1.5">Teacher Remarks &amp; Feedback {readOnly ? "" : "(Optional)"}</p>
        {readOnly ? <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="remarks-text">{remarks || "—"}</p>
          : <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} data-testid="remarks-input" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm outline-none focus:border-indigo-500" placeholder="Feedback for the student" />}
      </div>
      <div className="m-4 rounded-xl bg-[#141a33] text-white p-4" data-testid="marks-total">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Current Subjective Total Score</p>
        <p className="font-display text-3xl font-black text-emerald-400">{fmtM(given)} / {total ? fmtM(total) : "—"} <span className="text-lg">Marks</span></p>
        <p className="text-[11px] text-slate-300 mt-1">Questions marked: {marks.filter((m) => m != null).length} / {marks.length}</p>
      </div>
    </div>
  );
}
